/**
 * Aus der Videoebene ein Bild je Zeitpunkt.
 *
 * **Wofür das da ist:** die Relay-Seite bekommt von uns nur JSON und muss daraus ihre eigene
 * Ausgabe zeichnen (`docs/auftrag-relay-videoannotation.md`). Damit ihre Zeichnung nicht
 * unbemerkt von unserer abdriftet, legen wir Prüfvektoren ab — Ebenen samt dem SVG, das unser
 * Code daraus macht. Die drei Schritte dorthin stehen hier.
 *
 * ⚠️ **Hier wird nichts gezeichnet.** Das Markup kommt Zeichen für Zeichen aus
 * `annotation.ts::overlayInnerSvg`, derselben Funktion, die die Foto-Ebene schreibt. Eine
 * zweite Zeichenroutine wäre genau die Drift, gegen die die Vektoren helfen sollen.
 */

import { overlayInnerSvg, type Marker } from './annotation';
import { bloeckeBei, type VideoOverlay } from './videoAnnotation';

/**
 * Die Marken, die am Zeitpunkt `ms` gelten — in Stapelfolge, die letzte liegt oben.
 *
 * ⚠️ **Die Reihenfolge ist die der Datei**, nicht die des Editors. Dort liegt das ausgewählte
 * Band oben (`features/videoannotate/sicht.ts`), aber das ist eine Hervorhebung für den
 * Prüfer: Relay kennt keine Auswahl. Blöcke in Dateireihenfolge, Marken in Blockreihenfolge —
 * die einzige Regel, die auf beiden Seiten dasselbe Bild ergibt.
 */
export function markenBei(overlay: VideoOverlay, ms: number): Marker[] {
  return bloeckeBei(overlay.blocks, ms).flatMap((b) => b.markers);
}

/**
 * Die Zeitpunkte, an denen sich das Bild ändert — und an denen etwas zu sehen ist.
 *
 * Es sind genau die `fromMs`- und `toMs`-Werte aller Blöcke: dazwischen ändert sich nichts,
 * gesucht werden muss also nie.
 *
 * ⚠️ **Zeitpunkte mit leerer Ebene fallen weg.** Am Ende eines alleinstehenden Blocks gälte
 * nichts mehr, und ein leeres Standbild gehört in keinen Bericht.
 *
 * ⚠️ **Geprüft wird auf Leere, nicht auf «ist kein `fromMs`».** Endet ein Pfeilblock mitten in
 * einem längeren Kreisblock, ist sein Ende kein Anfang — das Bild ändert sich dort trotzdem,
 * der Pfeil geht und der Kreis bleibt. Die Abkürzung über `fromMs` würde diesen Zeitpunkt
 * verschlucken.
 */
export function aenderungsZeitpunkte(overlay: VideoOverlay): number[] {
  const roh = overlay.blocks.flatMap((b) => [b.fromMs, b.toMs]);

  return [...new Set(roh)].sort((a, b) => a - b).filter((ms) => markenBei(overlay, ms).length > 0);
}

/**
 * Die geltende Ebene als eigenständiges SVG, in der Grösse des Videobildes.
 *
 * ⚠️ **Ohne `data-relay-overlay` und `data-image`.** Beim Foto tragen die beiden die Bedeutung
 * «bearbeitbare Ebene genau dieses Bildes»; nur damit nimmt `svgToOverlay` eine Datei an. Ein
 * Standbild einer Videoebene ist beides nicht, und es soll auch nicht dafür gehalten werden.
 */
export function svgBei(overlay: VideoOverlay, ms: number): string {
  return (
    '<svg xmlns="http://www.w3.org/2000/svg"' +
    ' viewBox="0 0 ' + overlay.width + ' ' + overlay.height + '">' +
    overlayInnerSvg({
      width: overlay.width,
      height: overlay.height,
      markers: markenBei(overlay, ms),
    }) +
    '</svg>'
  );
}
